/**
 * Utility functions for generating idempotency keys for API requests
 */

/**
 * Generates a unique idempotency key using crypto.randomUUID()
 * Falls back to timestamp + random number if crypto is not available
 * 
 * @param prefix - Optional prefix for the key (default: 'idem')
 * @returns A unique idempotency key string
 */
export const generateIdempotencyKey = (prefix: string = 'idem'): string => {
  // Use crypto.randomUUID() if available (preferred method)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  
  // Fallback for environments without crypto.randomUUID
  // Use crypto.getRandomValues() for secure randomness
  const timestamp = Date.now().toString(36);
  const randomBytes = new Uint32Array(2);
  crypto.getRandomValues(randomBytes);
  const randomPart = randomBytes[0].toString(36);
  const counterPart = randomBytes[1].toString(36);
  
  return `${prefix}_${timestamp}_${randomPart}_${counterPart}`;
};

/**
 * Validates if a string looks like a valid idempotency key
 * 
 * @param key - The key to validate
 * @returns True if the key appears to be valid
 */
export const isValidIdempotencyKey = (key: string): boolean => {
  if (!key || typeof key !== 'string') {
    return false;
  }
  
  // Check if it's not empty and has reasonable length
  return key.length > 0 && key.length <= 100;
};
